import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Megaphone, Trash2, Rocket, Pause, Play, Loader2, X, Clapperboard } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));
const SYNC_ROLES = ["super_admin", "admin", "campaign_manager"];

export default function Campaigns() {
  const navigate = useNavigate();
  const user = auth.getUser();
  const canSync = SYNC_ROLES.includes(user?.role);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [mappings, setMappings] = useState({});
  const [advertisers, setAdvertisers] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pushTarget, setPushTarget] = useState(null);
  const [pushAdvertiser, setPushAdvertiser] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/campaigns"), api.get("/clients")]);
    setCampaigns(a.data); setClients(b.data);
    if (canSync) {
      api.get("/stackadapt/sync/mappings").then(r => {
        setMappings(Object.fromEntries((r.data || []).map(m => [m.local_campaign_id, m])));
      }).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openPush = async (c, e) => {
    e.stopPropagation();
    setPushTarget(c);
    if (advertisers.length === 0) {
      try {
        const r = await api.get("/stackadapt/advertisers");
        setAdvertisers(r.data.advertisers || []);
      } catch { toast.error("Could not load ad accounts"); }
    }
  };

  const doPush = async () => {
    if (!pushAdvertiser) { toast.error("Select an ad account"); return; }
    setBusy(pushTarget.id);
    try {
      const r = await api.post(`/stackadapt/sync/campaigns/${pushTarget.id}/push`, { advertiser_id: pushAdvertiser });
      toast.success(`Synced live — platform campaign #${r.data.mapping.sa_campaign_id}`);
      setPushTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed — see Sync Log");
    } finally { setBusy(null); }
  };

  const pauseResume = async (c, action, e) => {
    e.stopPropagation();
    setBusy(c.id);
    try {
      await api.post(`/stackadapt/sync/campaigns/${c.id}/${action}`);
      toast.success(action === "pause" ? "Paused on the ad platform" : "Resumed on the ad platform");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${action} failed`);
    } finally { setBusy(null); }
  };

  const syncAds = async (c, e) => {
    e.stopPropagation();
    const ids = c.creatives || [];
    if (ids.length === 0) { toast.error("No creatives attached to this campaign"); return; }
    setBusy(c.id);
    try {
      const r = await api.post(`/stackadapt/sync/campaigns/${c.id}/ads`, { creative_ids: ids });
      const { synced, failed } = r.data;
      if (synced.length) toast.success(`${synced.length} ad(s) live on the ad platform`);
      if (failed.length) toast.error(`${failed.length} failed: ${failed[0].error}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ad sync failed");
    } finally { setBusy(null); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/campaigns/${id}`); load();
  };

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.business_name]));
  const filtered = campaigns.filter(c =>
    (!filter || (c.name || "").toLowerCase().includes(filter.toLowerCase())) &&
    (!statusFilter || c.status === statusFilter)
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Campaign Management"
        title="Campaigns"
        subtitle="Multi-channel programmatic campaigns executed live — CTV, video, display, audio, DOOH."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => navigate("/campaigns/ctv/new")} data-testid="ctv-shortcut-btn">
              <Plus className="w-4 h-4" /> New CTV campaign
            </Btn>
            <Btn onClick={() => navigate("/campaigns/new")} data-testid="new-campaign-btn">
              <Plus className="w-4 h-4" /> New campaign
            </Btn>
          </div>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input placeholder="Filter by name…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid="campaign-filter" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="campaign-status-filter">
            <option value="">All statuses</option>
            {["Draft","Pending approval","Active","Paused","Completed","Rejected"].map(s => <option key={s}>{s}</option>)}
          </select>
          <div className="ml-auto text-xs text-slate-400">{filtered.length} of {campaigns.length}</div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={Megaphone} title="No campaigns" description="Launch your first multi-channel campaign." action={<Btn onClick={() => navigate("/campaigns/new")}>Create campaign</Btn>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="py-3 pr-4">Campaign</th>
                  <th className="py-3 pr-4">Client</th>
                  <th className="py-3 pr-4">Channels</th>
                  <th className="py-3 pr-4">Budget</th>
                  <th className="py-3 pr-4">Status</th>
                  {canSync && <th className="py-3 pr-4">Live Sync</th>}
                  <th className="py-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const m = mappings[c.id];
                  return (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate(`/campaigns/${c.id}`)}>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-white">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.category}{c.category === "Political" && " · ⚖ compliance"}</div>
                      </td>
                      <td className="py-3 pr-4 text-slate-300">{clientMap[c.client_id] || "—"}</td>
                      <td className="py-3 pr-4 text-slate-400 text-xs">{(c.channels || []).slice(0, 3).join(" · ") || "—"}{c.channels?.length > 3 && ` +${c.channels.length - 3}`}</td>
                      <td className="py-3 pr-4 font-mono">{usd(c.total_budget)}</td>
                      <td className="py-3 pr-4"><Pill status={c.status} /></td>
                      {canSync && (
                        <td className="py-3 pr-4" onClick={e => e.stopPropagation()}>
                          {m ? (
                            <div className="flex items-center gap-1.5">
                              <span className="pill pill-active text-[10px]" data-testid={`sa-synced-${c.id}`}>Live #{m.sa_campaign_id}</span>
                              {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> : (
                                <>
                                  {c.status === "Paused" ? (
                                    <button title="Resume live campaign" onClick={(e) => pauseResume(c, "resume", e)} className="p-1 text-slate-400 hover:text-emerald-300" data-testid={`sa-resume-${c.id}`}><Play className="w-3.5 h-3.5" /></button>
                                  ) : (
                                    <button title="Pause live campaign" onClick={(e) => pauseResume(c, "pause", e)} className="p-1 text-slate-400 hover:text-amber-300" data-testid={`sa-pause-${c.id}`}><Pause className="w-3.5 h-3.5" /></button>
                                  )}
                                  <button title="Sync ads (creatives) live" onClick={(e) => syncAds(c, e)} className="p-1 text-slate-400 hover:text-violet-300" data-testid={`sa-sync-ads-${c.id}`}><Clapperboard className="w-3.5 h-3.5" /></button>
                                  <button title="Re-sync" onClick={(e) => openPush(c, e)} className="p-1 text-slate-400 hover:text-cyan-300" data-testid={`sa-resync-${c.id}`}><Rocket className="w-3.5 h-3.5" /></button>
                                </>
                              )}
                            </div>
                          ) : (
                            <Btn variant="secondary" className="!px-2.5 !py-1 text-xs" onClick={(e) => openPush(c, e)} data-testid={`sa-push-${c.id}`}>
                              <Rocket className="w-3 h-3" /> Push
                            </Btn>
                          )}
                        </td>
                      )}
                      <td className="py-3 pr-4 text-right">
                        <button onClick={(e) => handleDelete(c.id, e)} className="text-slate-500 hover:text-rose-300 p-1" data-testid={`delete-campaign-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pushTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPushTarget(null)}>
          <div className="glass-card rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="push-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-bold">Push live</h3>
                <p className="text-xs text-slate-400 mt-1">“{pushTarget.name}” will be created live on the ad platform{pushTarget.status !== "Active" && " as a draft"}.</p>
              </div>
              <button onClick={() => setPushTarget(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <Select label="Ad account" value={pushAdvertiser} onChange={e => setPushAdvertiser(e.target.value)} data-testid="push-advertiser-select">
              <option value="">Select advertiser…</option>
              {advertisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
            <div className="mt-2 text-[11px] text-slate-500">
              Budget {usd(pushTarget.total_budget)} · CPM bid {pushTarget.cpm_bid ? `$${pushTarget.cpm_bid}` : "$25 default"} · Channel {(pushTarget.channels || [])[0] || "CTV"}
              {pushTarget.status === "Active" && <span className="text-amber-300"> · Will be LIVE (not draft)</span>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setPushTarget(null)}>Cancel</Btn>
              <Btn onClick={doPush} disabled={busy === pushTarget.id} data-testid="confirm-push-btn">
                {busy === pushTarget.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Push live
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
